import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Table, Form, Container, Row, Col, Button } from 'react-bootstrap';
import PaginationComponent from '../Pagination/PaginationComponent';
import { getAllFarms } from '../../services/Farm/farmService';
import CreateFarmModal from './CreateFarmModal';
import EditFarmModal from './EditFarmModal';
import FarmFilter from '../Filters/FarmFilter';
import { FaEdit } from 'react-icons/fa';
import { usePermissions } from '../../hooks/usePermissions';
import { usePermissionsContext } from '../../context/PermissionsContext';

const Farm = () => {
  const { checkPermission, ToastComponent, hasPermission } = usePermissions();
  const { permissions } = usePermissionsContext();
  const [persons, setFarms] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [numberOfElements, setNumberOfElements] = useState(0);
  const [showCreateFarmModal, setShowCreateFarmModal] = useState(false);
  const [showEditFarmModal, setShowEditFarmModal] = useState(false);
  const [selectedFarm, setSelectedFarm] = useState(null);
  const [filterParams, setFilterParams] = useState({});
  const previousFilterParams = useRef({});

  const canView = useMemo(() => hasPermission('farmView'), [hasPermission, permissions]);
  const canCreate = useMemo(() => hasPermission('farmCreate'), [hasPermission, permissions]);
  const canUpdate = useMemo(() => hasPermission('farmUpdate'), [hasPermission, permissions]);

  const fetchFarms = useCallback(async (filters = filterParams, currentPage = page, currentPageSize = pageSize) => {
    if (!canView) return;
    try {
      const data = await getAllFarms(filters, currentPage - 1, currentPageSize);
      setFarms(data.content);
      setTotalPages(data.totalPages);
      setTotalElements(data.totalElements);
      setNumberOfElements(data.numberOfElements);
    } catch (error) {}
  }, [filterParams, page, pageSize, canView]);

  useEffect(() => {
    if (canView) {
      fetchFarms();
    }
  }, [page, pageSize, fetchFarms, canView]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value, 10));
    setPage(1);
  };

  const handleOpenCreateFarmModal = () => {
    if (!checkPermission('farmCreate')) {
      return;
    }
    setShowCreateFarmModal(true);
  };
  const handleCloseCreateFarmModal = () => setShowCreateFarmModal(false);

  const handleOpenEditFarmModal = (farm) => {
    if (!checkPermission('farmUpdate')) {
      return;
    }
    setSelectedFarm(farm);
    setShowEditFarmModal(true);
  };
  
  const handleCloseEditFarmModal = () => {
    setSelectedFarm(null);
    setShowEditFarmModal(false);
  };
  
  const handleFilter = (filters) => {
    if (!canView) return;
    const filtersChanged = JSON.stringify(previousFilterParams.current) !== JSON.stringify(filters);

    if (filtersChanged) {
      setFilterParams(filters);
      setPage(1);
      fetchFarms(filters, 1, pageSize);
      previousFilterParams.current = filters;
    }
  };

  const handleClearFilter = () => {
    if (!canView) return;
    setFilterParams({});
    setPage(1);
    fetchFarms({}, 1, pageSize);
    previousFilterParams.current = {};
  };

  if (!canView) {
    return (
      <>
        <ToastComponent />
        <Container>
          <h3 className="mb-4 mt-2 text-center text-md-start">Fazendas</h3>
        </Container>
      </>
    );
  }

  return (
    <Container>
      <ToastComponent />
      <h3 className="mb-4 mt-2 text-center text-md-start">Fazendas</h3>
      <Row className="justify-content-between align-items-center mb-3">
        <Col xs={12} md="auto" className="d-flex align-items-center justify-content-md-end justify-content-center">
          <Form.Label className="me-2 mb-0">Mostrar</Form.Label>
          <Form.Select
            value={pageSize}
            onChange={handlePageSizeChange}
            style={{ width: 'auto' }}
            className="me-2"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </Form.Select>
        </Col>
        <Col xs={12} md="auto" className="d-flex justify-content-md-end justify-content-center mt-2 mt-md-0">
          {canCreate && (
            <Button variant="primary" className="button-custom" onClick={handleOpenCreateFarmModal}>
              Cadastrar Fazenda
            </Button>
          )}
        </Col>
      </Row>
      {canView && <FarmFilter onFilter={handleFilter} onClear={handleClearFilter} />}
      <div style={{ overflowX: 'auto' }}>
        <Table striped bordered hover responsive="sm">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Documento</th>
              <th>Proprietário</th>
              <th style={{textAlign: 'center', width: '50px' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {persons.map((farm) => (
              <tr key={farm.id}>
                <td>{farm.name}</td>
                <td>{farm.document}</td>
                <td>{farm.person !== null ? farm.person.name : ''}</td>
                <td style={{textAlign: 'center'}}>
                  {canUpdate && (
                    <Button
                      variant="link"
                      onClick={() => handleOpenEditFarmModal(farm)}
                      className="p-0"
                      style={{ fontSize: '1.2rem' }}
                    >
                      <FaEdit className='icon-custom'/>
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <PaginationComponent
        totalPages={totalPages}
        currentPage={page}
        onPageChange={handlePageChange}
        totalElements={totalElements}
        size={pageSize}
        pageNumber={page - 1}
        numberOfElements={numberOfElements}
      />

      <CreateFarmModal
        show={showCreateFarmModal}
        handleClose={handleCloseCreateFarmModal}
        refreshFarms={fetchFarms}
      />

      <EditFarmModal
        show={showEditFarmModal}
        handleClose={handleCloseEditFarmModal}
        farm={selectedFarm}
        refreshFarms={fetchFarms}
      />
    </Container>
  );
};

export default Farm;